import type { Locale } from '../../preferences/i18n';
import type { ArchiveSignature, GraphArchivePackage } from '../../../types/archive';

export const AUTH_SESSION_STORAGE_KEY = 'journeynodes.auth';
const GOOGLE_SCRIPT_ID = 'google-identity-services-client';
const GOOGLE_SCRIPT_SRC = 'https://accounts.google.com/gsi/client';

export interface AuthUser {
	email: string;
	name?: string;
}

export interface FullModeAuthSession {
	accessMode: 'unlocked';
	expiresAt: string;
	sessionToken: string;
	user: AuthUser;
}

export interface AuthUnlockSuccess extends FullModeAuthSession {
	success: true;
	message: string;
}

export interface AuthUnlockFailure {
	success: false;
	accessMode: 'preview';
	message: string;
	user?: AuthUser;
}

export type AuthUnlockResult = AuthUnlockSuccess | AuthUnlockFailure;

export interface GoogleCredentialResponse {
	credential?: string;
	select_by?: string;
}

export interface GoogleAuthConfig {
	clientId: string;
	workerUrl: string;
}

declare global {
	interface Window {
		google?: {
			accounts: {
				id: {
					initialize(options: { client_id: string; callback(response: GoogleCredentialResponse): void }): void;
					renderButton(
						container: HTMLElement,
						options: { locale?: string; shape?: string; size?: string; text?: string; theme?: string; type?: string }
					): void;
				};
			};
		};
	}
}

export function authConfigFromEnv(env: ImportMetaEnv = import.meta.env): GoogleAuthConfig | null {
	const clientId = env.VITE_GOOGLE_CLIENT_ID?.trim();
	const workerUrl = normalizeWorkerUrl(env.VITE_AUTH_WORKER_URL ?? '');

	if (!clientId || !workerUrl) {
		return null;
	}

	return { clientId, workerUrl };
}

function normalizeWorkerUrl(value: string): string {
	const workerUrl = value.trim().replace(/\/+$/, '');
	if (!workerUrl) return '';
	if (/^https?:\/\//i.test(workerUrl)) return workerUrl;

	const isLocalWorker = /^(localhost|127\.0\.0\.1|\[::1\])(?::\d+)?(?:\/|$)/i.test(workerUrl);
	return `${isLocalWorker ? 'http' : 'https'}://${workerUrl}`;
}

export function loadStoredAuthSession(storage: Storage = sessionStorage, now = new Date()): FullModeAuthSession | null {
	const raw = storage.getItem(AUTH_SESSION_STORAGE_KEY);
	if (!raw) return null;

	try {
		const session = JSON.parse(raw) as Partial<FullModeAuthSession>;
		if (
			session.accessMode !== 'unlocked'
			|| typeof session.sessionToken !== 'string'
			|| typeof session.expiresAt !== 'string'
			|| !session.user
			|| typeof session.user.email !== 'string'
		) {
			storage.removeItem(AUTH_SESSION_STORAGE_KEY);
			return null;
		}

		if (new Date(session.expiresAt).getTime() <= now.getTime()) {
			storage.removeItem(AUTH_SESSION_STORAGE_KEY);
			return null;
		}

		return {
			accessMode: 'unlocked',
			sessionToken: session.sessionToken,
			expiresAt: session.expiresAt,
			user: {
				email: session.user.email,
				name: typeof session.user.name === 'string' ? session.user.name : undefined,
			},
		};
	} catch {
		storage.removeItem(AUTH_SESSION_STORAGE_KEY);
		return null;
	}
}

export function saveAuthSession(session: FullModeAuthSession, storage: Storage = sessionStorage): void {
	storage.setItem(AUTH_SESSION_STORAGE_KEY, JSON.stringify(session));
}

export function clearAuthSession(storage: Storage = sessionStorage): void {
	storage.removeItem(AUTH_SESSION_STORAGE_KEY);
}

export async function loadGoogleIdentityScript(documentRef: Document = document): Promise<void> {
	if (window.google?.accounts?.id) return;

	const existingScript = documentRef.getElementById(GOOGLE_SCRIPT_ID) as HTMLScriptElement | null;
	if (existingScript) {
		await waitForGoogleIdentity();
		return;
	}

	await new Promise<void>((resolve, reject) => {
		const script = documentRef.createElement('script');
		script.id = GOOGLE_SCRIPT_ID;
		script.async = true;
		script.defer = true;
		script.src = GOOGLE_SCRIPT_SRC;
		script.onload = () => resolve();
		script.onerror = () => reject(new Error('Google Identity Services script failed to load.'));
		documentRef.head.append(script);
	});

	await waitForGoogleIdentity();
}

export function renderGoogleSignInButton({
	clientId,
	container,
	locale,
	onCredential,
}: {
	clientId: string;
	container: HTMLElement;
	locale: Locale;
	onCredential(idToken: string): void;
}): void {
	if (!window.google?.accounts?.id) {
		throw new Error('Google Identity Services is not loaded.');
	}

	container.replaceChildren();
	console.info('Initializing Google sign-in.', {
		origin: window.location.origin,
		clientId: maskGoogleClientId(clientId),
	});
	window.google.accounts.id.initialize({
		client_id: clientId,
		callback(response) {
			if (response.credential) {
				console.info('Google credential received; requesting full-mode unlock.');
				onCredential(response.credential);
				return;
			}

			console.warn('Google sign-in finished without returning a credential.', {
				selectBy: response.select_by,
			});
		},
	});
	window.google.accounts.id.renderButton(container, {
		locale: googleButtonLocale(locale),
		shape: 'rectangular',
		size: 'large',
		text: 'signin_with',
		theme: 'outline',
		type: 'standard',
	});
}

function maskGoogleClientId(clientId: string): string {
	const suffix = '.apps.googleusercontent.com';
	if (!clientId.endsWith(suffix)) return '(invalid format)';

	const prefix = clientId.slice(0, -suffix.length);
	const visiblePrefix = prefix.length <= 8 ? prefix : `${prefix.slice(0, 4)}...${prefix.slice(-4)}`;
	return `${visiblePrefix}${suffix}`;
}

export async function unlockWithGoogleCredential(idToken: string, workerUrl: string): Promise<AuthUnlockResult> {
	console.info('Posting Google credential to auth worker.', { workerUrl });
	const response = await fetch(`${workerUrl}/auth/google`, {
		method: 'POST',
		headers: { 'Content-Type': 'application/json' },
		body: JSON.stringify({ idToken }),
	});

	const payload = (await response.json()) as AuthUnlockResult;
	console.info('Auth worker responded to Google unlock.', {
		status: response.status,
		success: payload.success,
		message: payload.message,
	});

	if (response.ok && payload.success) {
		saveAuthSession(payload);
	}

	return payload;
}

export async function fetchProtectedArchive(
	filename: string,
	session: FullModeAuthSession,
	workerUrl: string
): Promise<ArrayBuffer> {
	const response = await fetch(`${workerUrl}/protected-archives/${encodeURIComponent(filename)}`, {
		headers: { Authorization: `Bearer ${session.sessionToken}` },
	});

	if (!response.ok) {
		throw new Error(await protectedArchiveErrorMessage(response));
	}

	const archiveBuffer = await response.arrayBuffer();
	if (!looksLikeZipArchive(archiveBuffer)) {
		throw new Error(await unexpectedProtectedArchiveMessage(response, archiveBuffer));
	}

	return archiveBuffer;
}

async function protectedArchiveErrorMessage(response: Response): Promise<string> {
	try {
		const payload = (await response.json()) as { message?: unknown };
		if (typeof payload.message === 'string' && payload.message.trim()) {
			return payload.message;
		}
	} catch {
		// Fall back to the status message below when the Worker does not return JSON.
	}

	return `Protected archive request failed with status ${response.status}`;
}

function looksLikeZipArchive(buffer: ArrayBuffer): boolean {
	const bytes = new Uint8Array(buffer);
	return bytes.length >= 4 && bytes[0] === 0x50 && bytes[1] === 0x4b && (
		(bytes[2] === 0x03 && bytes[3] === 0x04)
		|| (bytes[2] === 0x05 && bytes[3] === 0x06)
		|| (bytes[2] === 0x07 && bytes[3] === 0x08)
	);
}

async function unexpectedProtectedArchiveMessage(response: Response, archiveBuffer: ArrayBuffer): Promise<string> {
	const bytes = new Uint8Array(archiveBuffer);
	const prefix = new TextDecoder().decode(bytes.slice(0, 64)).replace(/\s+/g, ' ').trim();
	const contentType = response.headers.get('Content-Type') ?? 'unknown content type';
	const url = protectedArchiveUrlSummary(response.url);

	if (prefix.startsWith('UEsD')) {
		return `Protected archive response is Base64 text instead of zip bytes from ${url}. Redeploy the auth Worker so it decodes PROTECTED_ARCHIVES values before returning them.`;
	}

	const detail = prefix ? ` Response starts with "${prefix.slice(0, 48)}".` : '';
	return `Protected archive response was not a .journeynodes zip file from ${url} (${response.status}, ${contentType}, ${bytes.length} bytes).${detail}`;
}

function protectedArchiveUrlSummary(url: string): string {
	try {
		const parsed = new URL(url);
		return `${parsed.origin}${parsed.pathname}`;
	} catch {
		return url;
	}
}

export function createWorkerArchiveSigningClient(session: FullModeAuthSession, workerUrl: string) {
	return {
		async sign(archive: GraphArchivePackage): Promise<ArchiveSignature> {
			const response = await fetch(`${workerUrl}/archives/sign`, {
				method: 'POST',
				headers: {
					Authorization: `Bearer ${session.sessionToken}`,
					'Content-Type': 'application/json',
				},
				body: JSON.stringify({ archive: archiveSigningSnapshot(archive) }),
			});

			const payload = (await response.json()) as { success?: boolean; signature?: ArchiveSignature; message?: string };
			if (!response.ok || !payload.success || !payload.signature) {
				throw new Error(payload.message ?? `Archive signing failed with status ${response.status}`);
			}

			return payload.signature;
		},

		async verify(archive: GraphArchivePackage, signature: ArchiveSignature): Promise<boolean> {
			const response = await fetch(`${workerUrl}/archives/verify`, {
				method: 'POST',
				headers: {
					Authorization: `Bearer ${session.sessionToken}`,
					'Content-Type': 'application/json',
				},
				body: JSON.stringify({ archive: archiveSigningSnapshot(archive), signature }),
			});

			const payload = (await response.json()) as { success?: boolean; valid?: boolean };
			return response.ok && payload.success === true && payload.valid === true;
		},
	};
}

function googleButtonLocale(locale: Locale): string {
	return locale === 'zh-TW' ? 'zh_TW' : 'en';
}

async function waitForGoogleIdentity(): Promise<void> {
	for (let attempt = 0; attempt < 50; attempt += 1) {
		if (window.google?.accounts?.id) return;
		await new Promise((resolve) => window.setTimeout(resolve, 50));
	}

	throw new Error('Google Identity Services is unavailable.');
}

function archiveSigningSnapshot(archive: GraphArchivePackage) {
	return {
		graphJson: archive.graphJson,
		mediaManifest: archive.mediaManifest,
		configJson: archive.configJson,
		savegameJson: archive.savegameJson,
		assetNames: Object.keys(archive.assets).sort(),
	};
}
